/*
 * Copyright (c) 2023 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Injectable } from '@angular/core';
import { AbstractResourceService, AdditionalParams, QueryParams } from '@irian-ro/abstract-resource';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EnvironmentService } from '@irian-ro/util-environment';
import { Observable } from 'rxjs';
import { ETransportConfirmationDtoModel, ETransportListResponseVO, ETransportNotificationDTO, ETransportShipmentOrganizerNotificationListVo, EtransportModifVehicleDtoModel } from '@well/delivery-libs/etransport/domain-model';

@Injectable({
  providedIn: 'root',
})
export class ETransportIntegrationResourceService extends AbstractResourceService<
  ETransportNotificationDTO,
  ETransportNotificationDTO,
  any,
  any
> {
  constructor(httpClient: HttpClient, environmentService: EnvironmentService) {
    super(
      httpClient,
      environmentService.getEnvironmentProperty('eTransportIntegrationUrl'),
      ETransportNotificationDTO,
      ETransportNotificationDTO
    );
  }

  upload(uuid: string): Observable<any> {
    const headers = new HttpHeaders({
      'x-skip-error-interceptor': 'true',
    });
    return this.httpClient?.post(`${this.url}/upload/${uuid}`, {}, { headers: headers }).pipe(this.getDtoMapper());
  }

  check(uuid: string): Observable<any> {
    return this.httpClient?.post(`${this.url}/check/${uuid}`, {}).pipe(this.getDtoMapper());
  }

  checkAction(latestActionUuid: string): Observable<any> {
    return this.httpClient?.post(`${this.url}/check-action/${latestActionUuid}`, {}).pipe(this.getDtoMapper());
  }

  cancel(uuid: string): Observable<any> {
    const headers = new HttpHeaders({
      'x-skip-error-interceptor': 'true',
    });
    return this.httpClient?.post(`${this.url}/cancel/${uuid}`, {}, { headers: headers }).pipe(this.getDtoMapper());
  }

  modifVehicle(modifVehicleDto: EtransportModifVehicleDtoModel): Observable<any> {
    return this.httpClient?.post(`${this.url}/modif-vehicle`, modifVehicleDto).pipe(this.getDtoMapper());
  }

  uploadConfirmAction(latestActionUuid: string) {
    const headers = new HttpHeaders({
      'x-skip-error-interceptor': 'true',
    });
    return this.httpClient?.post(`${this.url}/upload-confirmation/${latestActionUuid}`, {}, { headers: headers })
      .pipe(this.getDtoMapper());
  }

  uploadCancelAction(latestActionUuid: string) {
    const headers = new HttpHeaders({
      'x-skip-error-interceptor': 'true',
    });
    return this.httpClient?.post(`${this.url}/upload-cancel/${latestActionUuid}`, {}, { headers: headers })
      .pipe(this.getDtoMapper());
  }

  uploadModifVehicleAction(latestActionUuid: string) {
    const headers = new HttpHeaders({
      'x-skip-error-interceptor': 'true',
    });
    return this.httpClient?.post(`${this.url}/upload-modif-vehicle/${latestActionUuid}`, {}, { headers: headers })
      .pipe(this.getDtoMapper());
  }

  confirmation(eTransportConfirmationDtoModel: ETransportConfirmationDtoModel) {
    return this.httpClient?.post(`${this.url}/confirmation`, eTransportConfirmationDtoModel).pipe(this.getDtoMapper());
  }

  getShipmentOrganizerList(additionalParams?: AdditionalParams[]): Observable<ETransportShipmentOrganizerNotificationListVo> {
    const queryString = new QueryParams({}, additionalParams || []).toString();
    return this.httpClient?.get<ETransportShipmentOrganizerNotificationListVo>(`${this.url}/list/shipment-organizer${queryString}`);
  }

  bulkSendNotifications(uuids: string[]) {
    return this.httpClient?.post(`${this.url}/upload/list/${uuids}`, {});
  }

  bulkCheckNotifications(uuids: string[]) {
    return this.httpClient?.post(`${this.url}/check/list/${uuids}`, {});
  }

  getList() : Observable<ETransportListResponseVO> {
    return this.httpClient?.get<ETransportListResponseVO>(`${this.url}/list`);
  }
}
